import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { CustomerAllocationData } from '../../../models/wizard-plugin-models/customer-allocation-data';
import { NotificationService } from '../../../services/notification.service';
import { Router } from '@angular/router';
import { ICourseEntityService } from '../../../interfaces/entities/course-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN, COURSE_ENTITY_SERVICE_TOKEN, CUSTOMER_ENTITY_SERVICE_TOKEN, REQUISITION_ENTITY_SERVICE_TOKEN, TRAINER_ENTITY_SERVICE_TOKEN } from '../../../injection-tokens/entity-service-tokens';
import { ClassesEntity } from '../../../models/entities/classes-entity';
import { TrainingSchedule, Requisition } from '../../../models/entities/requisition-model';
import { IClassesEntityService } from '../../../interfaces/entities/classes-entity-service.interface';
import { IRequisitionEntityService } from '../../../interfaces/entities/requisition-entity-service-interface';
import { ICustomerEntityService } from '../../../interfaces/entities/customer-entity-service.interface';
import { ITrainerEntityService } from '../../../interfaces/entities/trainer-entity-service.interface';
import { CustomerEntity } from '../../../models/entities/customer';
import { TrainerEntity } from '../../../models/entities/trainer';
import { ErrorHandlerService } from '../../../decoration-base/error-handler.service';
import { ResourceType } from '../../../models/resource-meta-data';
import { ResourceCreatorService } from '../../../services/resource-creator.service';
import { Log, IShortLogger } from '../../../services/logger.service';


interface ScheduleItem {
  title: string;
  trainingType: string;
  startDate: Date | null;
  endDate: Date | null;
  startTime: string;
  endTime: string;
  offDates: string;
  participants: string[];
  trainers: string[];
  resourceTemplate: string;
  courseId: string;
  deletionType: string;
  deletionDate: Date | null;
  deletionTime: string;
}

type ResourceLite = {
  id: string;
  name: string;
  type: string;
};

@Component({
  selector: 'app-requisition',
  templateUrl: './requisition.component.html',
  styleUrls: ['./requisition.component.css']
})
export class RequisitionComponent implements OnInit {

  requisitionForm!: FormGroup;
  statusOptions = ['Draft', 'Submitted', 'In Review', 'Approved', 'Rejected', 'Completed'];
  customerTypes = ['Corporate', 'Individual', 'Educational Institution', 'Government', 'Non-profit'];
  paymentTermsOptions = ['Advance Payment', 'Net 15 Days', 'Net 30 Days', 'Net 45 Days', 'Net 60 Days'];
  courseOptions = [    { id: '-1', name: 'Custom Course' }
  ];
  deletionTypeOptions = ['Immediate', 'Scheduled', 'Manual'];

  activeTabIndex: number = 0;

  resourceTypes = [
    { value: 'vm', label: 'Virtual Machine' },
    { value: 'db', label: 'Cloud DB' },
    { value: 'app', label: 'App Service' },
    { value: 'acc', label: 'User Account' }
  ];

  resourceTemplatesByType: { [key: string]: ResourceLite[] } = {
    vm: [],
    db: [],
    app: [],
    acc: []
  };

  private readonly logger: IShortLogger<RequisitionComponent> = Log.create(RequisitionComponent);

  constructor(private fb: FormBuilder,
    private router: Router,
    private resourceCreatorService: ResourceCreatorService,
    @Inject(COURSE_ENTITY_SERVICE_TOKEN) private courseService: ICourseEntityService,
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private classService: IClassesEntityService,
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN) private customerService: ICustomerEntityService,
    @Inject(TRAINER_ENTITY_SERVICE_TOKEN) private trainerService: ITrainerEntityService,
    @Inject(REQUISITION_ENTITY_SERVICE_TOKEN) private reqService: IRequisitionEntityService,
    private errorHandler: ErrorHandlerService,
    private notification: NotificationService
  ) {
    // Don't call createForm here - FormBuilder might not be ready
  }

  async ngOnInit(): Promise<void> {
    // this.courseOptions = [...this.courseService.getAllCourses().map(course => ({
    //   id: course.id,
    //   name: course.name
    // })), ...this.courseOptions];

     this.courseOptions = [...this.courseOptions];

    this.loadCustomers();
    this.loadTrainers();
    // Initialize the form here where FormBuilder is guaranteed to be available
    this.createForm();

    // Set default values
    const today = new Date();
    this.requisitionForm.patchValue({
      requisition: {
        docDate: this.formatDate(today),
        docNo: `REQ-${Math.floor(1000 + Math.random() * 9000)}`,
        status: 'Draft'
      }
    });

    // Listen for status changes to update badge
    this.requisitionForm.get('requisitcion.status')?.valueChanges.subscribe(status => {
      this.updateStatusBadge(status);
    });

    // Initialize sections as collapsed
    this.initializeSectionsAsCollapsed();
    if (!this.pluginData.removalOption) this.pluginData.removalOption = 'immediate';

    ['vm', 'db', 'app', 'acc'].forEach(async type => {
      this.resourceTemplatesByType[type] = (await this.resourceCreatorService.getResourcesByType(type as ResourceType))
        .map(resource => ({
          id: resource.id,
          name: resource.data['TemplateDetailsData']?.templateName ?? resource.metaData.name,
          type: resource.metaData.type
        }));
    });

       localStorage.setItem("userName",'Test 1');

  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private createForm() {
    this.requisitionForm = this.fb.group({
      requisition: this.fb.group({
        docNo: ['', Validators.required],
        docDate: ['', Validators.required],
        status: ['Draft', Validators.required]
      }),
      customerDetails: this.fb.group({
        customer: ['', Validators.required],
        customerType: ['Corporate', Validators.required],
        contactPerson: ['', Validators.required],
        contactNo: ['', Validators.required],
        emailId: ['', [Validators.required, Validators.email]]
      }),
      billingDetails: this.fb.group({
        billingAddress: ['', Validators.required],
        gstNo: [''],
        paymentTerms: ['Advance Payment', Validators.required],
        estimateAmount: ['', [Validators.required, Validators.min(0)]]
      }),
      trainingSchedules: this.fb.array([this.createScheduleGroup()]),
      additionalInfo: this.fb.group({
        specialRequirements: [''],
        finalComments: [''],
      })
    });
  }

  private createScheduleGroup(): FormGroup {
    return this.fb.group({
      title: ['', Validators.required],
      startDate: ['', Validators.required],
      endDate: ['', Validators.required],
      startTime: ['', Validators.required],
      endTime: ['', Validators.required],
      offDates: [''],
      participants: [''],
      trainers: [''],
      resourceType: [''], // This field stores the selected resource type
      resourceTemplate: [''],
      courseId: ['', Validators.required],
      deletionType: ['None'],
      deletionDate: [''],
      deletionTime: [''],
      // Dynamic resource controls will be added here
      vmControls: this.fb.group({}), // For VM specific controls
      cloudDbControls: this.fb.group({}), // For CloudDB specific controls
      appServiceControls: this.fb.group({}), // For AppService specific controls
      userAccountControls: this.fb.group({}), // For UserAccount specific controls
      _resourceValues: this.fb.control({}) // Hidden control to store resource values
    });
  }

  // Tab management methods
  setActiveTab(index: number): void {
    this.activeTabIndex = index;
  }

  get schedules(): FormArray {
    return this.requisitionForm.get('trainingSchedules') as FormArray;
  }

  getSelectedResourceType(scheduleIndex: number): string {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('resourceType')?.value || '';
  }

  getResourceTemplatesForType(resourceType: string): ResourceLite[] {
    return this.resourceTemplatesByType[resourceType as keyof typeof this.resourceTemplatesByType] || [];
  }

  addNewTemplate(scheduleIndex: number): void {
    const selectedType = this.getSelectedResourceType(scheduleIndex);
    if (selectedType) {
      // Logic to open template creation form/dialog would go here
      alert(`Launch ${selectedType} template creation form`);
    }
  }

  removeSchedule(index: number): void {
    if (this.schedules.length > 1) {
      this.schedules.removeAt(index);

      // Adjust active tab if necessary
      if (this.activeTabIndex >= this.schedules.length) {
        this.activeTabIndex = this.schedules.length - 1;
      } else if (this.activeTabIndex > index) {
        this.activeTabIndex--;
      }
    }
  }

  private updateStatusBadge(status: string): void {
    // This will be handled via class bindings in the template
  }

  toggleSection(sectionElement: HTMLElement): void {
    const title = sectionElement.querySelector('.section-title');
    const content = sectionElement.querySelector('.section-content');

    if (title && content) {
      title.classList.toggle('expanded');
      content.classList.toggle('expanded');
    }
  }

  private initializeSectionsAsCollapsed(): void {
    setTimeout(() => {
      const allSections = document.querySelectorAll('.form-section');
      allSections.forEach(section => {
        const title = section.querySelector('.section-title');
        const content = section.querySelector('.section-content');

        if (title && content) {
          title.classList.remove('expanded');
          content.classList.remove('expanded');
        }
      });
    }, 0);
  }

  addParticipant(scheduleIndex: number, input: HTMLInputElement): void {
    const value = input.value.trim();
    if (!value) return;

    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentParticipants = schedule.get('participants')?.value || '';

    // Check if participant already exists
    const existingParticipants = currentParticipants.split(',').map((p: string) => p.trim());
    if (existingParticipants.includes(value)) {
      input.value = '';
      return; // Don't add duplicate
    }

    const newParticipants = currentParticipants ?
      `${currentParticipants},${value}` : value;

    schedule.patchValue({ participants: newParticipants });
    input.value = '';
  }

  addTrainer(scheduleIndex: number, input: HTMLInputElement): void {
    const value = input.value.trim();
    if (!value) return;

    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentTrainers = schedule.get('trainers')?.value || '';

    // Check if trainer already exists
    const existingTrainers = currentTrainers.split(',').map((t: string) => t.trim());
    if (existingTrainers.includes(value)) {
      input.value = '';
      return; // Don't add duplicate
    }

    const newTrainers = currentTrainers ?
      `${currentTrainers},${value}` : value;

    schedule.patchValue({ trainers: newTrainers });
    input.value = '';
  }


  // addParticipant(scheduleIndex: number, input: HTMLInputElement): void {
  //   if (!input.value) return;

  //   const schedule = this.schedules.at(scheduleIndex) as FormGroup;
  //   const currentParticipants = schedule.get('participants')?.value || '';
  //   const newParticipants = currentParticipants ?
  //     `${currentParticipants},${input.value}` : input.value;

  //   schedule.patchValue({ participants: newParticipants });
  //   input.value = '';
  // }

  removeParticipantChip(scheduleIndex: number, participant: string): void {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentParticipants = (schedule.get('participants')?.value || '').split(',');
    const updatedParticipants = currentParticipants
      .filter((p: string) => p.trim() !== participant.trim())
      .join(',');

    schedule.patchValue({ participants: updatedParticipants });
  }

  // addTrainer(scheduleIndex: number, input: HTMLInputElement): void {
  //   if (!input.value) return;

  //   const schedule = this.schedules.at(scheduleIndex) as FormGroup;
  //   const currentTrainers = schedule.get('trainers')?.value || '';
  //   const newTrainers = currentTrainers ?
  //     `${currentTrainers},${input.value}` : input.value;

  //   schedule.patchValue({ trainers: newTrainers });
  //   input.value = '';
  // }

  removeTrainerChip(scheduleIndex: number, trainer: string): void {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentTrainers = (schedule.get('trainers')?.value || '').split(',');
    const updatedTrainers = currentTrainers
      .filter((t: string) => t.trim() !== trainer.trim())
      .join(',');

    schedule.patchValue({ trainers: updatedTrainers });
  }

  createNewResource(): void {
    // Logic to open resource creation form/dialog would go here
    alert('Launch resource creation form');
  }

  saveAsDraft(): void {
    // Logic to save form as draft would go here
    this.logger.i('Saving as draft:', this.requisitionForm.value);
  }

  // submitRequisition(): void {

  //   // Submit form logic would go here
  //   //todo Instead of new Requisition use form values to create requisition object
  //   const requisiton = this.reqService.createRequisition(new Requisition(), "admin");
  //   this.createClassForRequisition(requisiton);

  // }

  // Add this mapping configuration at the top of your component class
  private resourceTypeConfig = {
    vm: { controlGroup: 'vmControls', templateField: 'vmTemplate' },
    db: { controlGroup: 'cloudDbControls', templateField: 'dbType' },
    app: { controlGroup: 'appServiceControls', templateField: 'servicePlan' },
    acc: { controlGroup: 'userAccountControls', templateField: 'accountType' }
  };

  private getResourceTemplateId(scheduleData: any): string | null {
    const resourceType = scheduleData.resourceType;

    // Return null if no resource type is selected
    const config = this.resourceTypeConfig[resourceType as keyof typeof this.resourceTypeConfig];

    // Use optional chaining and nullish coalescing for clean extraction
    return config ? (scheduleData[config.controlGroup]?.[config.templateField] ?? null) : null;
  }

  async submitRequisition(): Promise<void> {
    // if (this.requisitionForm.invalid) {
    //   this.markFormGroupAsTouched(this.requisitionForm);
    //   this.notification.notify('Please fix validation errors before submitting.', 'warning', 3000);
    //   return;
    // }

    const formValue = this.requisitionForm.value;

    const newReq = new Requisition();

    // Document metadata
    newReq.docDate = new Date(formValue.requisition.docDate);
    newReq.status = formValue.requisition.status;

    // Customer Details
    newReq.customerDetails.id = formValue.customerDetails.customer;
    newReq.customerDetails.customerType = formValue.customerDetails.customerType;
    newReq.customerDetails.contactPerson.name = formValue.customerDetails.contactPerson;
    newReq.customerDetails.contactPerson.mobile = formValue.customerDetails.contactNo;
    newReq.customerDetails.contactPerson.email = formValue.customerDetails.emailId;

    const selectedCustomer = this.customers.find((c: { id: any; }) => c.id === formValue.customerDetails.customer);
    if (selectedCustomer) {
      newReq.customerDetails.name = selectedCustomer.name;
      newReq.customerDetails.address = selectedCustomer.address;
      newReq.customerDetails.contactPerson.email = selectedCustomer.contactPerson.email;
      newReq.customerDetails.contactPerson.mobile = selectedCustomer.contactPerson.mobile;
    }

    // Billing Details
    newReq.billingDetails.billingAddress = formValue.billingDetails.billingAddress;
    newReq.billingDetails.gstNo = formValue.billingDetails.gstNo;
    newReq.billingDetails.paymentTerms = formValue.billingDetails.paymentTerms;
    newReq.billingDetails.estimateAmount = formValue.billingDetails.estimateAmount;

    // Additional Info
    newReq.specialRequirements = formValue.additionalInfo.specialRequirements;
    newReq.finalComments = formValue.additionalInfo.finalComments;

    // Training Schedules
    newReq.trainingSchedules = formValue.trainingSchedules.map((sched: any, index: number) => {
      const ts = new TrainingSchedule();
      ts.srNo = index + 1;
      ts.title = sched.title;
      ts.startDate = new Date(sched.startDate);
      ts.endDate = new Date(sched.endDate);
      ts.startTime = sched.startTime;
      ts.endTime = sched.endTime;

      // offDates string → Date[]
      ts.offDates = (sched.offDates || '')
        .split(',')
        .map((d: string) => d.trim())
        .filter((d: any) => d)
        .map((d: string) => new Date(d));

      // participants from comma-separated string
      const pEmails = (sched.participants || '')
        .split(',')
        .map((e: string) => e.trim())
        .filter((e: any) => e);

      ts.participants = pEmails.map((email: string) => ({
        email,
        redemptionKey: this.generateRandomKey()
      }));

      // trainers (if needed in model, currently left empty)
      const trainer = this.trainerService.getTrainerById(sched.trainers);
      ts.trainers = [];
      if (trainer)
        ts.trainers.push(trainer);

      ts.course = this.courseService.getCourseById(sched.courseId);


      const resourceTemplateId = this.getResourceTemplateId(sched);
      ts.resourceTemplateIds = resourceTemplateId ? [resourceTemplateId] : [];

      this.logger.i('Resource Template IDs:', ts.resourceTemplateIds);

      // Resource template
      // ts.resourceTemplateIds = sched.resourceTemplate ? [sched.resourceTemplate] : [];
      // this.logger.i('Resource Template IDs:', ts.resourceTemplateIds);

      // Deletion Policy
      ts.resourcesManagementPolicies.deletion.deletionType = sched.deletionType;
      if (sched.deletionDate) {
        ts.resourcesManagementPolicies.deletion.deletionDate = new Date(sched.deletionDate);
      }
      ts.resourcesManagementPolicies.deletion.deletionTime = sched.deletionTime || '';

      this.logger.i('Training Schedule:', ts);

      return ts;
    });

    // Save via service
    const createdReq = await this.reqService.createRequisition(newReq, 'admin');

    this.logger.i('Created Requisition:', createdReq);
    // Create classes
    this.createClassForRequisition(createdReq);

    // Notify success
    this.notification.notify('Requisition submitted successfully.', 'success', 3000);

    // Optionally reset form or navigate away
    // this.requisitionForm.reset();
    // this.router.navigate(['/requisitions']);
  }

  generateRandomKey(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = crypto.getRandomValues(new Uint8Array(1))[0] % 16;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }


  cancelRequisition(): void {
    // Logic to cancel or reset form would go here
    if (confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      this.createForm();
      this.ngOnInit();
    }
  }

  private markFormGroupAsTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupAsTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(item => {
          if (item instanceof FormGroup) {
            this.markFormGroupAsTouched(item);
          }
        });
      }
    });
  }

  getParticipantsArray(scheduleIndex: number): string[] {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const participantsStr = schedule.get('participants')?.value || '';
    return participantsStr.split(',').filter((p: string) => p.trim() !== '');
  }

  getTrainersArray(scheduleIndex: number): string[] {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const trainersStr = schedule.get('trainers')?.value || '';
    return trainersStr.split(',').filter((t: string) => t.trim() !== '');
  }

  addSchedule(): void {
    const newIndex = this.schedules.length + 1;
    const scheduleGroup = this.createScheduleGroup();

    // Set default title
    scheduleGroup.patchValue({
      title: `Training Schedule ${newIndex}`
    });

    // Add the new schedule to the form array
    this.schedules.push(scheduleGroup);

    // Switch to the new tab
    this.activeTabIndex = this.schedules.length - 1;

    // Initialize with default resource type if needed
    // Uncomment the line below if you want a default resource type
    // this.selectResourceType(this.activeTabIndex, 'VM');
  }

  removeCurrentSchedule(): void {
    if (this.schedules.length > 1) {
      this.schedules.removeAt(this.activeTabIndex);

      // Adjust active tab if necessary
      if (this.activeTabIndex >= this.schedules.length) {
        this.activeTabIndex = this.schedules.length - 1;
      }
    } else {
      alert('At least one training schedule is required.');
    }
  }

  // Resource type management with dynamic controls
  selectResourceType(scheduleIndex: number, resourceType: string): void {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    const currentResourceType = schedule.get('resourceType')?.value;
    this.logger.i(currentResourceType);
    this.logger.i(resourceType);

    ['vm', 'db', 'app', 'acc'].forEach(async type => {
      this.resourceTemplatesByType[type] = (await this.resourceCreatorService.getResourcesByType(type as ResourceType))
        .map(resource => ({
          id: resource.id,
          name: resource.data['TemplateDetailsData']?.templateName ?? resource.metaData.name,
          type: resource.metaData.type
        }));
    });


    // Only clear and recreate controls if the resource type actually changed
    if (currentResourceType !== resourceType) {
      // Store current values before clearing
      const currentValues = this.getCurrentResourceValues(schedule, currentResourceType);

      schedule.patchValue({
        resourceType: resourceType,
        resourceTemplate: '' // Reset template when type changes
      });

      // Clear all resource-specific controls
      this.clearResourceControls(schedule);

      // Add controls based on selected type
      this.addResourceTypeControls(schedule, resourceType);

      // If switching back to a previously used type, restore its values
      if (currentValues[resourceType]) {
        this.restoreResourceValues(schedule, resourceType, currentValues[resourceType]);
      }
    }
  }

  private getCurrentResourceValues(schedule: FormGroup, resourceType: string): any {
    const storedValues = schedule.get('_resourceValues')?.value || {};

    if (resourceType) {
      const controlGroupName = this.getControlGroupName(resourceType);
      const controlGroup = schedule.get(controlGroupName) as FormGroup;
      if (controlGroup) {
        storedValues[resourceType] = controlGroup.value;
      }
    }

    // Store back to form (create the control if it doesn't exist)
    if (!schedule.get('_resourceValues')) {
      schedule.addControl('_resourceValues', this.fb.control(storedValues));
    } else {
      schedule.get('_resourceValues')?.setValue(storedValues);
    }

    return storedValues;
  }

  // Helper method to restore resource values
  private restoreResourceValues(schedule: FormGroup, resourceType: string, values: any): void {
    const controlGroupName = this.getControlGroupName(resourceType);
    const controlGroup = schedule.get(controlGroupName) as FormGroup;
    if (controlGroup && values) {
      controlGroup.patchValue(values);
    }
  }

  // Helper method to get control group name based on resource type
  private getControlGroupName(resourceType: string): string {
    switch (resourceType) {
      case 'vm': return 'vmControls';
      case 'db': return 'cloudDbControls';
      case 'app': return 'appServiceControls';
      case 'acc': return 'userAccountControls';
      default: return '';
    }
  }

  private clearResourceControls(schedule: FormGroup): void {
    const resourceControlGroups = ['vmControls', 'cloudDbControls', 'appServiceControls', 'userAccountControls'];
    resourceControlGroups.forEach(groupName => {
      const controlGroup = schedule.get(groupName) as FormGroup;
      Object.keys(controlGroup.controls).forEach(key => {
        controlGroup.removeControl(key);
      });
    });
  }

  private addResourceTypeControls(schedule: FormGroup, resourceType: string): void {
    switch (resourceType) {
      case 'vm':
        this.addVMControls(schedule);
        break;
      case 'db':
        this.addCloudDBControls(schedule);
        break;
      case 'app':
        this.addAppServiceControls(schedule);
        break;
      case 'acc':
        this.addUserAccountControls(schedule);
        break;
    }
  }

  private addVMControls(schedule: FormGroup): void {
    const vmGroup = schedule.get('vmControls') as FormGroup;
    vmGroup.addControl('vmTemplate', this.fb.control('Angular Developer VM'));
    vmGroup.addControl('operatingSystem', this.fb.control('Windows Server 2019'));
    vmGroup.addControl('diskSize', this.fb.control('128'));
    vmGroup.addControl('networkConfig', this.fb.control('Default'));
    vmGroup.addControl('additionalSoftware', this.fb.control(''));
  }

  private addCloudDBControls(schedule: FormGroup): void {
    const cloudDbGroup = schedule.get('cloudDbControls') as FormGroup;
    cloudDbGroup.addControl('dbType', this.fb.control('MySQL Template'));
    cloudDbGroup.addControl('dbVersion', this.fb.control('8.0'));
    cloudDbGroup.addControl('storageSize', this.fb.control('20'));
    cloudDbGroup.addControl('performanceTier', this.fb.control('Basic'));
    cloudDbGroup.addControl('backupRetention', this.fb.control('7'));
  }

  private addAppServiceControls(schedule: FormGroup): void {
    const appServiceGroup = schedule.get('appServiceControls') as FormGroup;
    appServiceGroup.addControl('servicePlan', this.fb.control('Web App Template'));
    appServiceGroup.addControl('runtime', this.fb.control('.NET 6'));
    appServiceGroup.addControl('customDomain', this.fb.control(''));
    appServiceGroup.addControl('sslEnabled', this.fb.control(true));
    appServiceGroup.addControl('environmentVariables', this.fb.control(''));
  }

  private addUserAccountControls(schedule: FormGroup): void {
    const userAccountGroup = schedule.get('userAccountControls') as FormGroup;
    userAccountGroup.addControl('accountType', this.fb.control('Admin Account'));
    userAccountGroup.addControl('permissions', this.fb.control('Read-Write'));
    userAccountGroup.addControl('expirationDate', this.fb.control(''));
    userAccountGroup.addControl('defaultPassword', this.fb.control(''));
    userAccountGroup.addControl('requirePasswordChange', this.fb.control(true));
  }

  // Helper methods to get control values
  getVMControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('vmControls') as FormGroup;
  }

  getCloudDBControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('cloudDbControls') as FormGroup;
  }

  getAppServiceControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('appServiceControls') as FormGroup;
  }

  getUserAccountControls(scheduleIndex: number): FormGroup {
    const schedule = this.schedules.at(scheduleIndex) as FormGroup;
    return schedule.get('userAccountControls') as FormGroup;
  }

  public pluginData: CustomerAllocationData = new CustomerAllocationData();
  public startDateValid: boolean = true;
  public endDateValid: boolean = true;
  public removalDateValid: boolean = true;

  setRemovalOption(option: 'immediate' | 'scheduled' | 'manual'): void {
    this.pluginData.removalOption = option;

    if (option === 'manual') {
      // Clear removal date time when manual is selected
      this.pluginData.removalDateTime = null;
    } else if (option === 'scheduled' && !this.pluginData.removalDateTime) {
      // Default to 1 day after the allocation end date, or tomorrow if no end date
      const base = this.pluginData.endDate || new Date();
      const defaultRemovalDate = new Date(base);
      defaultRemovalDate.setDate(defaultRemovalDate.getDate() + 1);
      this.pluginData.removalDateTime = defaultRemovalDate;
    }

    // Re-validate
    // this.validateRemovalDate();
  }

  // Helper method to calculate deletion time (end date + active end time + 1 hour)
  getDeletionTime(): string {
    if (!this.pluginData.endDate) {
      return "1 hour after allocation ends";
    }

    // Create a new date from the end date
    const deletionDate = new Date(this.pluginData.endDate);

    // Set the hours to activeEnd + 1
    deletionDate.setHours(this.pluginData.activeEnd + 1, 0, 0, 0);

    // Format the date
    return this.formatDateTimeForDisplay(deletionDate);
  }

  // Helper to format date and time for display
  formatDateTimeForDisplay(date: Date): string {
    const month = date.toLocaleString('default', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();

    // Format time (12-hour format with AM/PM)
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${month} ${day}, ${year} at ${hours}:${minutes} ${ampm}`;
  }

  // Helper method to get minimum date for removal date (day after end date)
  get minRemovalDate(): Date {
    if (!this.pluginData.endDate) {
      return new Date();
    }

    // Always return a date that's one day after the end date
    const minRemoval = new Date(this.pluginData.endDate);
    minRemoval.setDate(minRemoval.getDate() + 1);
    return minRemoval;
  }

  validateRemovalDate(): void {
    if (this.pluginData.removalOption !== 'scheduled') {
      this.removalDateValid = true;
      return;
    }

    if (!this.pluginData.removalDateTime) {
      this.removalDateValid = false;
      return;
    }

    if (!this.pluginData.endDate) {
      this.removalDateValid = true;
      return;
    }

    // Removal date must be after end date
    this.removalDateValid = new Date(this.pluginData.removalDateTime) > new Date(this.pluginData.endDate);
  }

  createNewVMTemplate() {

    const fullUrl = `${window.location.origin}/#/wizard/Virtual Machine`;
    this.router.navigate([]).then((result) => { window.open(fullUrl, '_blank'); });
    // this.router.navigate(['/wizard', 'Virtual Machine']);

  }

  createClassForTrainingSchedules(schedules: TrainingSchedule[], customerId: string): ClassesEntity[] {

    let classes: ClassesEntity[] = [];
    schedules.forEach(sch => this.classService.createClass(sch, customerId));
    return classes;
  }

  createClassForRequisition(requisition: Requisition): ClassesEntity[] {
    return this.createClassForTrainingSchedules(requisition.trainingSchedules, requisition.customerDetails.id);
  }
  public customers: any;//CustomerEntity[] = [];
  public trainers: TrainerEntity[] = [];

  // loadCustomers1(): void {
  //   this.errorHandler.tryExecuteAsync(async () => {
  //     this.customers = this.customerService.getAllCustomers();
  //   }, 'loadCustomers');
  //   this.logger.i('Loaded customers:', this.customers);
  // }

  async loadCustomers(): Promise<void> {

    const allCustomers = await this.customerService.getAllCustomers();
    this.customers = allCustomers;
    this.logger.i('Loaded customers:', this.customers);
  }

  onCustomerChange(e: any) {

    const selectedCustomer = (e.target as HTMLSelectElement).value; // ✅

    if (selectedCustomer) {
      const customer = this.customers.find((c: { id: string; }) => c.id === selectedCustomer);
      if (customer) {
        this.requisitionForm.patchValue({
          customerDetails: {
            contactPerson: customer.contactPerson.name,
            contactNo: customer.contactPerson.mobile,
            emailId: customer.contactPerson.email
          },
          billingDetails: {
            billingAddress: customer.address,
            gstNo: customer.gstNumber,
          },
        });
      }
    } else {
      this.requisitionForm.patchValue({
        customerDetails: {
          customer: '',
          contactPerson: '',
          contactNo: '',
          emailId: ''
        }
      });
    }
  }
  loadTrainers(): void {
    this.trainers = this.trainerService.getAllTrainers();
  }

  sessionParams = {
    classId: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    isVisible: false
  };

  openClassSession() {
    // Set your parameters here
    this.sessionParams = {
      classId: 'CLASS-001',
      startDate: '2025-06-04',  // Format: YYYY-MM-DD
      endDate: '2025-06-13',    // Format: YYYY-MM-DD
      startTime: '12:00PM',
      endTime: '18:00PM',
      isVisible: true
    };
  }
}
